import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { GridItemSize } from '../../grid/GridItem/GridItem';

/**
 * use a pipeline of filters to filter the items.
 * The filters can then be rendered via the handle. (e.g. by passing it to the FilterBar)
 *
 * usage example
 *
 * const MyComponent = (props: { items: Array<any> }) => {
 *   const { items } = props;
 *
 *   const filters = useMemo(() => {
 *     return [
 *       {
 *         defaultValue: null,
 *         filter: (item, value) => item.x === value,
 *         render: ({ value, setValue }) => {
 *           return (
 *            <SearchableSelect
 *              ....
 *              value={value}
 *              onValueChanged={setValue}
 *            >
 *            </SearchableSelectProps>
 *           );
 *         }
 *       }
 *     ];
 *   }, []);
 *
 *   const { handle, filteredItems } = useFilterPipeline({ items, filters });
 *
 *   return (
 *     <div>
 *      <FilterBar handle={handle}></FilterBar>
 *      {filteredItems.map(item => generateJsxForItem(item))}
 *     </div>
 *   )
 * }
 *
 */
export function useFilterPipeline(t0) {
  const $ = _c(13);
  const {
    items,
    filters
  } = t0;
  const {
    getFilterContext,
    getFilterValueSetter
  } = useFilterContexts(filters);
  let t1;
  if ($[0] !== filters || $[1] !== getFilterContext || $[2] !== items) {
    let t2;
    if ($[4] !== getFilterContext) {
      t2 = (lastFilterResult, filter) => {
        const context = getFilterContext(filter);
        if (!context) {
          return lastFilterResult;
        }
        return lastFilterResult.filter(item => filter.filter(item, context.value));
      };
      $[4] = getFilterContext;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = filters.reduce(t2, items);
    $[0] = filters;
    $[1] = getFilterContext;
    $[2] = items;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const filteredEntities = t1;
  let t2;
  if ($[6] !== filters || $[7] !== getFilterContext || $[8] !== getFilterValueSetter) {
    t2 = {
      renderFilters: () => {
        const renderedFilters = [];
        for (const filter_0 of filters) {
          const context_0 = getFilterContext(filter_0);
          if (!context_0) {
            continue;
          }
          renderedFilters.push({
            id: context_0.id,
            element: <filter_0.Component value={context_0.value} setValue={getFilterValueSetter(filter_0)} />,
            size: filter_0.size ?? 2
          });
        }
        return renderedFilters;
      }
    };
    $[6] = filters;
    $[7] = getFilterContext;
    $[8] = getFilterValueSetter;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handle = t2;
  let t3;
  if ($[10] !== filteredEntities || $[11] !== handle) {
    t3 = {
      filteredItems: filteredEntities,
      handle
    };
    $[10] = filteredEntities;
    $[11] = handle;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function useFilterContexts(filters) {
  const $ = _c(12);
  let t0;
  if ($[0] !== filters) {
    t0 = () => new Map(filters.map(_temp));
    $[0] = filters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [filterContexts, setFilterContexts] = useState(t0);
  const filterValueSetters = useFilterValueSetters(filters, setFilterContexts);
  let t1;
  let t2;
  if ($[2] !== filters) {
    t1 = () => {
      setFilterContexts(oldFilterContexts => {
        const newFilterContexts = new Map(oldFilterContexts);
        let modified = false;
        for (const filter_0 of filters) {
          if (!newFilterContexts.has(filter_0)) {
            newFilterContexts.set(filter_0, createFilterContext(filter_0));
            modified = true;
          }
        }
        for (const key of newFilterContexts.keys()) {
          if (!filters.includes(key)) {
            newFilterContexts.delete(key);
            modified = true;
          }
        }
        return modified ? newFilterContexts : oldFilterContexts;
      });
    };
    t2 = [filters];
    $[2] = filters;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== filterContexts) {
    t3 = filter_1 => filterContexts.get(filter_1) ?? null;
    $[5] = filterContexts;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== filterValueSetters) {
    t4 = filter_2 => {
      const setter = filterValueSetters.get(filter_2);
      assertNotNullOrUndefined(setter, "no setter found");
      return setter;
    };
    $[7] = filterValueSetters;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = {
      getFilterContext: t3,
      getFilterValueSetter: t4
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp(filter) {
  return [filter, createFilterContext(filter)];
}
function useFilterValueSetters(filters, setFilterContexts) {
  const $ = _c(5);
  let t0;
  if ($[0] !== filters || $[1] !== setFilterContexts) {
    let t1;
    if ($[3] !== setFilterContexts) {
      t1 = filter => [filter, value => {
        setFilterContexts(oldFilterContexts => {
          const newFilterContexts = new Map(oldFilterContexts);
          const context = getRequiredContext(newFilterContexts, filter);
          newFilterContexts.set(filter, {
            ...context,
            value
          });
          return newFilterContexts;
        });
      }];
      $[3] = setFilterContexts;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = new Map(filters.map(t1));
    $[0] = filters;
    $[1] = setFilterContexts;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function getRequiredContext<T>(contexts: Map<FilterPipelineFilter<T, any>, FilterContext<any>>, filter: FilterPipelineFilter<T, any>): FilterContext<any> {
  const context = contexts.get(filter);
  assertNotNullOrUndefined(context, 'no context found');
  return context;
}
let lastFilterId = 0;
function createFilterContext<TValue>(filter: FilterPipelineFilter<unknown, TValue>): FilterContext<TValue> {
  return {
    id: ++lastFilterId,
    value: filter.defaultValue
  };
}
export type UseFilterPipelineOptions<T> = {
  items: Array<T>;
  filters: Array<FilterPipelineFilter<T, any>>;
};
export type UseFilterPipelineReturnValue<T> = {
  filteredItems: Array<T>;
  handle: FilterPipelineHandle;
};

/**
 * A single filter of the filter pipeline
 * Them same instance of the filter should be used for all renderings so they can be tracked correctly
 * This can be achieved with a useMemo or defining the filter globally
 */
export type FilterPipelineFilter<TItem, TValue> = {
  defaultValue: TValue;
  filter(item: TItem, value: TValue): boolean;
  Component(options: {
    value: TValue;
    setValue: (value: TValue) => void;
  }): React.JSX.Element;
  /**
   * default: 2
   */
  size?: GridItemSize;
};
export type FilterPipelineHandle = {
  renderFilters: () => Array<RenderedFilterPipelineFilter>;
};
export type RenderedFilterPipelineFilter = {
  id: number;
  size: GridItemSize;
  element: React.JSX.Element;
};
type FilterContext<TValue> = {
  id: number;
  value: TValue;
};